import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/ModeratorVideoManagement.css";
import RejectPopup from "./RejectVideoPopUp";
import SortByAndStatusSelectDropdowns from "./SortByAndStatusSelectDropdowns";
import AcceptVideoPopup from "./AcceptVideoPopup";
import rejected from "../images/rejected.png";
import approved from "../images/approved.png";
import comment from "../images/comment.png";
import videoPlay from "../images/videoPlay.png";
import close_icon from "../images/close_icon.png";
import {
  allVideos,
  pendingVideoRequests,
  approvedVideoRequests,
  rejectedVideoRequests,
  approveVideo,
  rejectVideo,
} from "../Services/AllServices";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";

const ModeratorVideoManagement = ({ searchQuery }) => {
  const [commentOpen, setCommentOpen] = useState(null);
  const [commentText, setCommentText] = useState(""); // State for comment text
  const [submitEnabled, setSubmitEnabled] = useState(false); // State to enable/disable submit button
  const [showSubmitButton, setShowSubmitButton] = useState(false); // To toggle submit button visibility
  const [videosAll, setVideosAll] = useState([]);
  const [filteredVideos, setFilteredVideos] = useState([]);
  const [openRejectPopup, setOpenRejectPopup] = useState(false);
  const [videoIdToReject, setVideoIdToReject] = useState(null);
  const [openApprovePopup, setOpenApprovePopup] = useState(false);
  const [videoIdToApprove, setVideoIdToApprove] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Fetch all videos initially
    fetchAllVideos();
  }, []);

  useEffect(() => {
    // Filter videos whenever searchQuery changes
    if (searchQuery) {
      const filtered = videosAll.filter(
        (video) =>
          video.title.toLowerCase().includes(searchQuery.toLowerCase()) || // Match title
          video.tags.some((tag) =>
            tag.toLowerCase().includes(searchQuery.toLowerCase())
          ) // Match tags
      );
      setFilteredVideos(filtered);
      console.log("Filtered Videos:", filtered);
    } else {
      setFilteredVideos(videosAll);
      console.log("All Videos:", videosAll); // If no search query, show all videos
    }
  }, [searchQuery, videosAll]);

  const fetchAllVideos = () => {
    allVideos((response) => {
      if (Array.isArray(response.data)) {
        const sortedVideos = response.data.sort((a, b) =>
          b._id.localeCompare(a._id)
        );
        setVideosAll(sortedVideos); // Set sorted videos
        setFilteredVideos(sortedVideos); // Set filtered videos to all initially
      } else {
        console.error("Data is not an array:", response);
      }
    });
  };

  const handleStatusSelect = (selectedStatus) => {
    switch (selectedStatus) {
      case "All":
        setFilteredVideos(videosAll); // Show all videos when "All" is selected
        break;
      case "Approved":
        approvedVideoRequests((response) => {
          if (Array.isArray(response.data)) {
            const sortedVideos = response.data.sort((a, b) => b.id - a.id);
            setFilteredVideos(sortedVideos);
          } else {
            console.error("Data is not an array:", response);
          }
        });
        break;
      case "Pending":
        pendingVideoRequests((response) => {
          if (Array.isArray(response.data)) {
            const sortedVideos = response.data.sort((a, b) => b.id - a.id);
            setFilteredVideos(sortedVideos);
          } else {
            console.error("Data is not an array:", response);
          }
        });
        break;
      case "Rejected":
        rejectedVideoRequests((response) => {
          if (Array.isArray(response.data)) {
            const sortedVideos = response.data.sort((a, b) => b.id - a.id);
            setFilteredVideos(sortedVideos);
          } else {
            console.error("Data is not an array:", response);
          }
        });
        break;
      default:
        break;
    }
  };

  const handleSort = (order) => {
    let sortedVideos;
    if (order === "Latest") {
      sortedVideos = [...videosAll].sort((a, b) => b._id.localeCompare(a._id));
    } else if (order === "Oldest") {
      sortedVideos = [...videosAll].sort((a, b) => a._id.localeCompare(b._id));
    }
    setFilteredVideos(sortedVideos);
  };
  const handleCommentChange = (e) => {
    const text = e.target.value;
    setCommentText(text);
    setSubmitEnabled(text.trim().length > 0); // Enable submit if text is entered
  };

  const toggleCommentBox = (videoId, showSubmit) => {
    setCommentOpen(commentOpen === videoId ? null : videoId);
    setShowSubmitButton(showSubmit); // Show the submit button only when necessary
    setCommentText(""); // Clear the comment box when opening a new one
  };

  const handleApproveButtonClick = (videoId) => {
    setVideoIdToApprove(videoId);
    setOpenApprovePopup(true);
  };

  const handleRejectButtonClick = (videoId) => {
    setCommentOpen(commentOpen === videoId ? null : videoId);
    setVideoIdToReject(videoId);
    setOpenRejectPopup(true);
  };

  const handleApprove = async () => {
    if (videoIdToApprove) {
      const videoId = videoIdToApprove;

      // Update the local state immediately
      const updatedVideos = videosAll.map((video) => {
        if (video.id === videoId) {
          return { ...video, videostatus: "approved" }; // Update status locally
        }
        return video;
      });

      setVideosAll(updatedVideos); // Update the videosAll state
      setFilteredVideos(updatedVideos); // Update the filtered videos state

      const formData = { videoId };
      const callback = (response) => {
        if (response && response.success) {
          console.log("Video approved successfully:", response);
          window.location.reload(); // Refresh page on approval
        } else {
          console.error("Approval failed:", response);
        }
      };

      try {
        await approveVideo(formData, callback);
      } catch (error) {
        console.error("Error approving video:", error);
      } finally {
        setOpenApprovePopup(false);
        setVideoIdToApprove(null);
      }
    }
  };

  const handleReject = async () => {
    if (videoIdToReject) {
      const videoId = videoIdToReject;

      // Update the local state immediately
      const updatedVideos = videosAll.map((video) => {
        if (video.id === videoId) {
          return { ...video, videostatus: "rejected", remarks: commentText }; // Update status and remarks locally
        }
        return video;
      });

      setVideosAll(updatedVideos); // Update the videosAll state
      setFilteredVideos(updatedVideos); // Update the filtered videos state

      const formData = {
        videoId: videoId,
        remarks: commentText, // Pass the comment text from state
      };

      const callback = (response) => {
        if (response && response.success) {
          console.log("Video rejected successfully:", response);
          window.location.reload(); // Refresh page on rejected
        } else {
          console.error("Rejection failed:", response);
        }
      };

      try {
        await rejectVideo(formData, callback);
      } catch (error) {
        console.error("Error rejecting video:", error);
      } finally {
        setOpenRejectPopup(false);
        setVideoIdToReject(null);
        setCommentText(""); // Clear the comment after submitting
      }
    }
  };

  const handleVideoClick = async (video) => {
    // console.log("Clicked video ID:", video._id);

    navigate("/videoplayer", { state: { video, videoData: videosAll } });
  };

  return (
    <div className="moderator-video-management">
      <div className="videos-container">
        <h1>Video Management</h1>

        {/* Dropdowns for filtering and sorting */}
        <SortByAndStatusSelectDropdowns
          onStatusChange={handleStatusSelect}
          onSortByChange={handleSort} // Pass the sorting function here
        />

        <h2>Videos</h2>

        <div className="moderator-videos-list">
          {filteredVideos.length === 0 ? (
            <div className="no-videos-message">No videos found</div>
          ) : (
            filteredVideos.map((video) => (
              <div
                key={video._id}
                className={`moderator-video-card ${video.videostatus.toLowerCase()}`}
              >
                <div
                  className="moderator-video_thumbnail"
                  onClick={() => handleVideoClick(video)}
                >
                  <img src={video.thumbnail.url} alt="Thumbnail" />
                </div>
                <div className="moderator-video_details">
                  <div
                    className="moderator-video_title_play"
                    onClick={() => handleVideoClick(video)}
                  >
                    <div className="moderator-video_title">{video.title}</div>
                    <div className="moderator-video_play">
                      <img src={videoPlay} alt="play" />
                    </div>
                  </div>
                  <div style={{ display: "flex" }}>
                    {/* <div className="moderator-video-time">
                      {video.time ? video.time : "NA"}
                    </div> */}
                    <div className="moderator-eye-toggle">
                      {video.publish.toLowerCase() === "private" ? (
                        <>
                          <FontAwesomeIcon
                            icon={faEyeSlash}
                            className="moderator-eye-icon"
                          />
                          <span>Private</span>
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon
                            icon={faEye}
                            className="moderator-eye-icon"
                          />
                          <span>Public</span>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="moderator-video-tags">
                    {Array.isArray(video.tags) && video.tags.length > 0
                      ? video.tags.map((tag, index) => (
                          <div key={index} className="moderator-tag">
                            {tag}
                          </div>
                        ))
                      : null}
                  </div>
                  <p>{video.description}</p>
                </div>
                <div className="moderator_videosAll_status_buttons">
                  {video.videostatus.toLowerCase() === "pending" && (
                    <div className="moderator_videos_actions">
                      <button
                        className="moderator_reject"
                        onClick={() => toggleCommentBox(video._id, true)}
                      >
                        Reject
                      </button>
                      <button
                        className="moderator_approve"
                        onClick={() => handleApproveButtonClick(video._id)}
                      >
                        Approve
                      </button>
                    </div>
                  )}

                  {video.videostatus.toLowerCase() === "approved" && (
                    <div className="moderator_status_label">
                      <div className="moderator_approve_img">
                        <img src={approved} alt="approve" />
                      </div>
                      <div>Approved</div>
                    </div>
                  )}

                  {video.videostatus.toLowerCase() === "rejected" && (
                    <div className="moderator_status_label">
                      <div className="moderator_reject_img">
                        <img src={rejected} alt="reject" />
                      </div>
                      <div>Rejected</div>
                      <div
                        className="moderator_comment_img"
                        onClick={() => toggleCommentBox(video._id, false)}
                      >
                        <img src={comment} alt="comment" />
                      </div>
                    </div>
                  )}
                </div>
                {commentOpen === video._id && (
                  <div className="comment-box-overlay">
                    <div className="comment-box">
                      <div className="comment-head-section">
                        <div className="comment-header">Comments</div>
                        <div
                          className="comment-close-icon"
                          onClick={() => toggleCommentBox(null)}
                        >
                          <img src={close_icon} alt="Close" />
                        </div>
                      </div>

                      {video.videostatus.toLowerCase() === "rejected" &&
                      !showSubmitButton ? (
                        <div className="comment-input-box">
                          <p>
                            {video.remarks
                              ? video.remarks
                              : "No remarks available"}
                          </p>
                        </div>
                      ) : (
                        <textarea
                          placeholder="Write your comment here..."
                          className="comment-input-box"
                          onChange={handleCommentChange}
                          value={commentText}
                        />
                      )}

                      {showSubmitButton && (
                        <button
                          className="comment-submit-button"
                          disabled={!submitEnabled}
                          onClick={() => handleRejectButtonClick(video._id)}
                        >
                          Submit
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        <RejectPopup
          open={openRejectPopup}
          onClose={() => setOpenRejectPopup(false)}
          onReject={handleReject}
        />
        <AcceptVideoPopup
          open={openApprovePopup}
          onClose={() => setOpenApprovePopup(false)}
          onApprove={handleApprove}
        />
      </div>
    </div>
  );
};

export default ModeratorVideoManagement;
